import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, SectionCard, Select, toast } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import type { AdminPaymentDetail } from '@/lib/api-admin/types';
import { ManualRefundModal } from '@/features/payments/components/ManualRefundModal';
import { useManualRefundMutation } from '@/features/payments/hooks/usePaymentActionMutation';
import type { LegacyPgInfo } from '@/features/payments/lib/refundFlags';
import { orderQueryKey } from '../hooks/useOrderQuery';

/**
 * QA-432 재요청 — 주문 상세 [결제상태 변경] 박스(드롭다운 + 버튼).
 * BE 에 결제상태를 임의로 바꾸는 API 는 없다 — 고를 수 있는 상태를 기존 처리에 잇는다(마일리지·쿠폰·재고 복원은 BE 가 한다).
 *   PAID      결제완료      ← 무통장 입금대기: 입금확인(confirm-deposit)
 *   REFUNDED  수기 취소     ← 토스 결제키가 없는 결제(고도몰 이관 모든 결제수단·수기주문·독립몰 무통장): 수기 환불 기록(manual-refund)
 *   CANCELED  토스 취소     ← 토스 결제(독립몰 카드·간편결제·가상계좌·계좌이체·휴대폰): 토스에서 취소한 결제를 다시 조회해 반영(payment-sync)
 * 이름은 결제 경로 기준(BE→BO 요청 2026-10-21) — BE 는 이 이름을 쓰지 않는다.
 */
export type PaymentStatusTarget = 'RESTORE' | 'PAID' | 'PG_RECOVER' | 'REFUNDED' | 'CANCELED';

export const PAYMENT_STATUS_TARGET_LABEL: Record<PaymentStatusTarget, string> = {
  RESTORE: '입금대기 (취소 복원)',
  PAID: '결제완료 (입금확인)',
  // QA-599 — 고객결제중단(카드·간편결제 등 PG) 주문을 토스에서 결제 완료 확인 후 결제완료로.
  PG_RECOVER: '결제완료 (PG 확인)',
  REFUNDED: '수기 취소 (고도몰 모든 결제수단·독립몰 무통장입금)',
  CANCELED: '토스 취소 (독립몰 카드·간편결제·가상계좌·계좌이체·휴대폰)',
};

const PAYMENT_STATUS_TARGET_HINT: Record<PaymentStatusTarget, string> = {
  RESTORE: '결제된 적 없는 무통장 취소 주문을 입금대기로 되돌립니다. 입금을 확인한 뒤 [결제완료 (입금확인)]으로 바꾸세요. 고객 알림은 가지 않습니다.',
  PAID: '고객 입금을 확인하고 결제완료로 바꿉니다.',
  PG_RECOVER: '토스에서 결제 완료를 확인하고 결제완료로 변경합니다. 무통장 직접입금은 [입금대기 (취소 복원)]을 쓰세요.',
  REFUNDED: '원 PG 관리자 취소·고객 계좌 이체 등 토스 밖에서 환불한 뒤 금액·방법을 기록합니다.',
  CANCELED: '토스 상점관리자에서 먼저 취소한 뒤 실행하세요. 토스 결제를 다시 조회해 반영합니다.',
};

export const OrderPaymentStatusBox = ({
  targets,
  onSelect,
}: {
  targets: ReadonlyArray<PaymentStatusTarget>;
  onSelect: (target: PaymentStatusTarget) => void;
}) => {
  const { t } = useTranslation('orders');
  const [target, setTarget] = useState<PaymentStatusTarget | ''>('');
  const selected = target && targets.includes(target) ? target : '';
  // 바꿀 상태가 없어도 카드는 보인다(QA-432 — "버튼이 어디 있냐" 문의) — 이유를 안내한다.
  if (targets.length === 0) {
    return (
      <SectionCard title={t('orders.paymentStatus.title', '결제상태 변경')} data-testid="order-payment-status-box">
        <p className="text-sm text-[color:var(--content-muted)]" data-testid="order-payment-status-empty">
          {t(
            'orders.paymentStatus.empty',
            '현재 주문 상태에서는 변경할 수 있는 결제상태가 없습니다. (결제 전·취소·만료·결제실패·구매확정 주문)',
          )}
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title={t('orders.paymentStatus.title', '결제상태 변경')} data-testid="order-payment-status-box">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select
          aria-label={t('orders.paymentStatus.select', '변경할 결제상태')}
          value={selected}
          onChange={(e) => setTarget(e.target.value as PaymentStatusTarget | '')}
          className="sm:w-64"
          data-testid="order-payment-status-select"
        >
          <option value="">{t('orders.paymentStatus.placeholder', '변경할 결제상태 선택')}</option>
          {targets.map((v) => (
            <option key={v} value={v}>
              {t(`orders.paymentStatus.targets.${v}`, PAYMENT_STATUS_TARGET_LABEL[v])}
            </option>
          ))}
        </Select>
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
          data-action="payment-status-change"
        >
          {t('orders.paymentStatus.apply', '변경')}
        </Button>
      </div>
      {selected ? (
        <p className="mt-2 text-[12px] text-[color:var(--content-muted)]" data-testid="order-payment-status-hint">
          {t(`orders.paymentStatus.hints.${selected}`, PAYMENT_STATUS_TARGET_HINT[selected])}
        </p>
      ) : null}
    </SectionCard>
  );
};

/** [수기 취소] — 결제 상세의 [수기 환불 기록] 팝업을 주문 상세에서 연다. 열릴 때만 마운트한다(쿼리 클라이언트 의존). */
export const OrderManualRefundDialog = ({
  orderId,
  payment,
  manualRefundOnly,
  allowedMethods,
  legacyPg,
  onClose,
}: {
  orderId: string;
  payment: AdminPaymentDetail;
  manualRefundOnly: boolean;
  allowedMethods?: ReadonlyArray<'ORIGINAL_PG' | 'BANK_TRANSFER' | 'OTHER'>;
  legacyPg: LegacyPgInfo | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation('orders');
  const qc = useQueryClient();
  const mutation = useManualRefundMutation();
  return (
    <ManualRefundModal
      payment={payment}
      isOpen
      isSubmitting={mutation.isPending}
      errorMessage={mutation.error ? friendlyMessage(mutation.error, t('orders.actionModal.unknownError')) : null}
      manualRefundOnly={manualRefundOnly}
      {...(allowedMethods ? { allowedMethods } : {})}
      legacyPg={legacyPg}
      onSubmit={(body, idempotencyKey) =>
        mutation.mutate(
          { paymentId: payment.paymentId, body, idempotencyKey },
          {
            onSuccess: async () => {
              toast.success(t('orders.paymentStatus.refunded', '수기 취소(환불)를 기록했습니다.'));
              onClose();
              // 주문 반영(클레임 완료·주문 취소)은 BE 가 이어서 처리한다 — 주문을 다시 불러온다.
              await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
              await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
            },
          },
        )
      }
      onCancel={onClose}
    />
  );
};
